import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, RefreshCw, Server, Database, ArrowLeft, Loader2 } from 'lucide-react';
import api from '../utils/api';

export default function StatusPage() {
  const [loading, setLoading] = useState(true);
  // operational | disruption | null(loading)
  const [state, setState] = useState(null);
  const [apiUp, setApiUp] = useState(false);
  const [dbUp, setDbUp] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);

  const check = useCallback(async () => {
    setLoading(true);
    try {
      // api baseURL is '/api' → resolves to GET /api/health
      const { data } = await api.get('/health');
      const isApiUp = data?.status === 'ok';
      const isDbUp = data?.db === 'up';
      setApiUp(isApiUp);
      setDbUp(isDbUp);
      setState(isApiUp && isDbUp ? 'operational' : 'disruption');
    } catch (err) {
      // 503 (degraded) or network failure — read body if present, else treat as fully down
      const body = err?.response?.data;
      setApiUp(false);
      setDbUp(body?.db === 'up');
      setState('disruption');
    } finally {
      setCheckedAt(new Date());
      setLoading(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const operational = state === 'operational';
  const StatusIcon = operational ? CheckCircle2 : XCircle;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/login" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft size={15} /> Back to login
          </Link>
        </div>

        {/* Overall status card */}
        <div
          className={`rounded-2xl border p-8 shadow-sm ${
            operational
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                operational ? 'bg-emerald-600' : 'bg-red-600'
              }`}
            >
              {loading
                ? <Loader2 size={22} className="text-white animate-spin" />
                : <StatusIcon size={22} className="text-white" />}
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${operational ? 'text-emerald-900' : 'text-red-900'}`}>
                {loading
                  ? 'Checking system status…'
                  : operational
                    ? 'All systems operational'
                    : 'Service disruption'}
              </h1>
              <p className={`text-sm mt-1 ${operational ? 'text-emerald-700' : 'text-red-700'}`}>
                {operational
                  ? 'Rostery is running normally.'
                  : 'One or more services are not responding as expected.'}
              </p>
            </div>
          </div>
        </div>

        {/* Component breakdown */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mt-6 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Components</p>

          <ComponentRow
            icon={Server}
            label="API service"
            up={apiUp}
            loading={loading}
          />
          <ComponentRow
            icon={Database}
            label="Database"
            up={dbUp}
            loading={loading}
          />
        </div>

        {/* Footer: timestamp + re-check */}
        <div className="flex items-center justify-between mt-6">
          <p className="text-xs text-slate-400">
            {checkedAt
              ? `Last checked ${checkedAt.toLocaleString()}`
              : 'Not yet checked'}
          </p>
          <button
            onClick={check}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Re-check
          </button>
        </div>
      </div>
    </div>
  );
}

function ComponentRow({ icon: Icon, label, up, loading }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <Icon size={16} className="text-slate-400" />
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </div>
      {loading ? (
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <Loader2 size={13} className="animate-spin" /> Checking
        </span>
      ) : up ? (
        <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
          <CheckCircle2 size={14} /> Operational
        </span>
      ) : (
        <span className="flex items-center gap-1.5 text-xs font-semibold text-red-600">
          <XCircle size={14} /> Down
        </span>
      )}
    </div>
  );
}
